import {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
} from "react";
import {
  useForm,
  useWatch,
  type UseFormRegister,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { CompanySelectField } from "./CompanySelectField";
import { resizeBrandImage } from "../utils/resizeBrandImage";
import {
  useBrandDetail,
  useUploadBrandLogo,
  useUpsertBrand,
} from "../hooks/useBrands";
import { useBrandStore } from "../stores/brandStore";
import { getDispatchCompany } from "@/features/company/services/companyService";
import type { DispatchCompanyListItem } from "@/features/company/types/company.types";
import type {
  BrandDetail,
  BrandUploadPurpose,
  ReqUpsertBrand,
} from "../types/brand.types";

const DELIVERY_CATEGORIES = ["quick", "mini", "cold", "general"] as const;
// 신규 브랜드 기본 노출은 콜드체인 제외 — cold는 명시 선택 시에만.
const DEFAULT_DELIVERY_CATEGORIES = ["quick", "mini", "general"] as const;

type BrandFormTab = "basic" | "display" | "asset";
const BRAND_FORM_TAB_ORDER: BrandFormTab[] = ["basic", "display", "asset"];

const brandSchema = z.object({
  companyGuid: z.string().optional(),
  brandId: z.string().optional(),
  host: z.string().trim().min(1, "대표 도메인(호스트)은 필수입니다"),
  // 추가 도메인 목록(대표 host 제외). 각 host는 전역 유일.
  domains: z.array(z.string()),
  name: z.string().trim().min(1, "표시명은 필수입니다"),
  ceoName: z.string().optional(),
  privacyOfficer: z.string().optional(),
  bizNo: z.string().optional(),
  licenseNo: z.string().optional(),
  address: z.string().optional(),
  addressDetail: z.string().optional(),
  phone: z.string().optional(),
  phoneDigits: z.string().optional(),
  copyright: z.string().optional(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  proposalFileName: z.string().optional(),
  proposalS3Key: z.string().optional(),
  logoHeaderUrl: z.string().optional(),
  logoAuthUrl: z.string().optional(),
  faviconUrl: z.string().optional(),
  ogImageUrl: z.string().optional(),
  orderMemoTag: z.string().optional(),
  logoAuthInvert: z.boolean(),
  isActive: z.boolean(),
  showQuickOrder: z.boolean(),
  quickOrderPricePolicy: z.enum(["STANDARD", "CONSULTATION"]),
  showRegister: z.boolean(),
  deliveryCategories: z
    .array(z.enum(DELIVERY_CATEGORIES))
    .min(1, "최소 1개 선택"),
});

type BrandFormValues = z.infer<typeof brandSchema>;
type DeliveryCategory = BrandFormValues["deliveryCategories"][number];

// 검증 실패 시 에러 필드가 속한 탭으로 자동 전환하기 위한 탭별 필드 매핑.
const TAB_FIELDS: Record<BrandFormTab, (keyof BrandFormValues)[]> = {
  basic: [
    "host",
    "domains",
    "isActive",
    "showQuickOrder",
    "showRegister",
    "deliveryCategories",
    "quickOrderPricePolicy",
  ],
  display: [
    "name",
    "ceoName",
    "privacyOfficer",
    "bizNo",
    "licenseNo",
    "phone",
    "phoneDigits",
    "address",
    "addressDetail",
  ],
  asset: [
    "logoHeaderUrl",
    "logoAuthUrl",
    "faviconUrl",
    "ogImageUrl",
    "proposalS3Key",
    "proposalFileName",
  ],
};
type BrandUrlFieldName =
  | "logoHeaderUrl"
  | "logoAuthUrl"
  | "faviconUrl"
  | "ogImageUrl"
  | "proposalS3Key";

const DEFAULT_FORM_VALUES: BrandFormValues = {
  companyGuid: "",
  brandId: "",
  host: "",
  domains: [],
  name: "",
  ceoName: "",
  privacyOfficer: "",
  bizNo: "",
  licenseNo: "",
  address: "",
  addressDetail: "",
  phone: "",
  phoneDigits: "",
  copyright: "",
  metaTitle: "",
  metaDescription: "",
  proposalFileName: "",
  proposalS3Key: "",
  logoHeaderUrl: "",
  logoAuthUrl: "",
  faviconUrl: "",
  ogImageUrl: "",
  orderMemoTag: "",
  logoAuthInvert: false,
  isActive: true,
  showQuickOrder: true,
  quickOrderPricePolicy: "STANDARD",
  showRegister: true,
  deliveryCategories: [...DEFAULT_DELIVERY_CATEGORIES],
};

const DELIVERY_CATEGORY_OPTIONS: {
  value: DeliveryCategory;
  label: string;
}[] = [
  { value: "quick", label: "퀵서비스" },
  { value: "mini", label: "다마스·라보" },
  { value: "cold", label: "콜드체인" },
  { value: "general", label: "일반화물" },
];

const VISIBLE_ASSET_FIELDS: {
  fieldName: BrandUrlFieldName;
  label: string;
  purpose: BrandUploadPurpose;
  accept: string;
}[] = [
  {
    fieldName: "logoHeaderUrl",
    label: "로고 (헤더/로그인 공통)",
    purpose: "BRAND_LOGO_HEADER",
    accept:
      ".jpg,.jpeg,.png,.webp,.svg,image/jpeg,image/png,image/webp,image/svg+xml",
  },
  {
    fieldName: "faviconUrl",
    label: "Favicon",
    purpose: "BRAND_FAVICON",
    accept:
      ".ico,.png,image/x-icon,image/vnd.microsoft.icon,image/png",
  },
  {
    fieldName: "ogImageUrl",
    label: "공유 미리보기 이미지 (선택)",
    purpose: "BRAND_OG",
    accept: ".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp",
  },
];

const ASSET_LIMITS: Record<
  BrandUrlFieldName,
  {
    maxBytes: number;
    types: readonly string[];
    extensions: readonly string[];
    description: string;
  }
> = {
  logoHeaderUrl: {
    maxBytes: 2 * 1024 * 1024,
    types: ["image/jpeg", "image/png", "image/webp", "image/svg+xml"],
    extensions: [".jpg", ".jpeg", ".png", ".webp", ".svg"],
    description:
      "JPG/PNG/WebP 최대 2MB(긴 변 400px 이하), SVG 최대 512KB",
  },
  logoAuthUrl: {
    maxBytes: 2 * 1024 * 1024,
    types: ["image/jpeg", "image/png", "image/webp", "image/svg+xml"],
    extensions: [".jpg", ".jpeg", ".png", ".webp", ".svg"],
    description: "로고와 동일한 파일로 저장",
  },
  faviconUrl: {
    maxBytes: 512 * 1024,
    types: ["image/x-icon", "image/vnd.microsoft.icon", "image/png"],
    extensions: [".ico", ".png"],
    description: "ICO/PNG, 최대 512KB, PNG는 긴 변 256px 이하로 저장",
  },
  ogImageUrl: {
    maxBytes: 3 * 1024 * 1024,
    types: ["image/jpeg", "image/png", "image/webp"],
    extensions: [".jpg", ".jpeg", ".png", ".webp"],
    description: "JPG/PNG/WebP, 최대 3MB, 긴 변 1200px 이하로 저장",
  },
  proposalS3Key: {
    maxBytes: 10 * 1024 * 1024,
    types: ["application/pdf"],
    extensions: [".pdf"],
    description: "PDF, 최대 10MB",
  },
};

// 폼 필드 → 업로드 Purpose 매핑. 저장 시 일괄 업로드에 사용.
const FIELD_PURPOSE: Record<BrandUrlFieldName, BrandUploadPurpose> = {
  logoHeaderUrl: "BRAND_LOGO_HEADER",
  logoAuthUrl: "BRAND_LOGO_AUTH",
  faviconUrl: "BRAND_FAVICON",
  ogImageUrl: "BRAND_OG",
  proposalS3Key: "BRAND_PROPOSAL",
};

type PendingFiles = Partial<Record<BrandUrlFieldName, File>>;

function optionalString(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${Math.floor(bytes / 1024 / 1024)}MB`;
  }
  return `${Math.floor(bytes / 1024)}KB`;
}

function fileExtension(fileName: string): string {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : "";
}

function validateBrandAssetFile(
  fieldName: BrandUrlFieldName,
  file: File,
): string | null {
  const limit = ASSET_LIMITS[fieldName];
  const extension = fileExtension(file.name);
  const isSvgLogo =
    fieldName === "logoHeaderUrl" &&
    (extension === ".svg" || file.type === "image/svg+xml");
  const hasAllowedType = file.type
    ? limit.types.includes(file.type)
    : false;
  const hasAllowedExtension = limit.extensions.includes(extension);

  if (!hasAllowedType && !hasAllowedExtension) {
    return `${limit.description} 파일만 등록할 수 있습니다.`;
  }

  if (isSvgLogo && file.size > 512 * 1024) {
    return "SVG 로고는 512KB 이하로 등록해 주세요.";
  }

  if (file.size > limit.maxBytes) {
    return `파일 용량은 ${formatBytes(limit.maxBytes)} 이하로 등록해 주세요.`;
  }

  return null;
}

// URL/도메인 입력 → 순수 host로 정규화 (소문자, 프로토콜·포트·경로 제거)
// 예: "https://b2b.nalo.kr/order" → "b2b.nalo.kr"
function normalizeHost(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  try {
    const withProtocol = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;
    return new URL(withProtocol).hostname.toLowerCase();
  } catch {
    // URL 파싱 실패 시 경로·포트만 제거하는 폴백
    return trimmed
      .replace(/^https?:\/\//i, "")
      .split("/")[0]
      .split(":")[0]
      .toLowerCase();
  }
}

function toFormValues(detail: BrandDetail): BrandFormValues {
  return {
    companyGuid: detail.companyGuid ?? "",
    brandId: detail.brandId,
    host: detail.host,
    domains: detail.domains ?? [],
    name: detail.name,
    ceoName: detail.ceoName ?? "",
    privacyOfficer: detail.privacyOfficer ?? "",
    bizNo: detail.bizNo ?? "",
    licenseNo: detail.licenseNo ?? "",
    address: detail.address ?? "",
    addressDetail: detail.addressDetail ?? "",
    phone: detail.phone ?? "",
    phoneDigits: detail.phoneDigits ?? "",
    copyright: detail.copyright ?? "",
    metaTitle: detail.metaTitle ?? "",
    metaDescription: detail.metaDescription ?? "",
    proposalFileName: detail.proposalFileName ?? "",
    // key는 서버가 내려주지 않음(presigned만). 빈 값 -> 재업로드 안 하면 백엔드가 기존 key 보존.
    proposalS3Key: "",
    logoHeaderUrl: "",
    logoAuthUrl: "",
    faviconUrl: "",
    ogImageUrl: "",
    orderMemoTag: detail.orderMemoTag ?? "",
    logoAuthInvert: detail.logoAuthInvert,
    isActive: detail.isActive,
    showQuickOrder: detail.showQuickOrder,
    quickOrderPricePolicy: detail.quickOrderPricePolicy,
    showRegister: detail.showRegister,
    deliveryCategories:
      detail.deliveryCategories?.filter(
        (category): category is DeliveryCategory =>
          DELIVERY_CATEGORIES.some((allowed) => allowed === category),
      ) ?? [...DEFAULT_DELIVERY_CATEGORIES],
  };
}

const COMPANY_COPY_SET_VALUE_OPTIONS = {
  shouldDirty: true,
  shouldValidate: true,
} as const;

function toUpsertRequest(values: BrandFormValues): ReqUpsertBrand {
  const primaryHost = values.host.trim();
  // 추가 도메인: 정규화 + 빈값·대표host중복 제거.
  const domains = Array.from(
    new Set(
      values.domains
        .map((d) => normalizeHost(d))
        .filter((d) => d && d !== primaryHost),
    ),
  );
  return {
    BrandId: optionalString(values.brandId),
    CompanyGuid: optionalString(values.companyGuid),
    Host: primaryHost,
    Domains: domains,
    Name: values.name.trim(),
    CeoName: optionalString(values.ceoName),
    PrivacyOfficer: optionalString(values.privacyOfficer),
    BizNo: optionalString(values.bizNo),
    LicenseNo: optionalString(values.licenseNo),
    Address: optionalString(values.address),
    AddressDetail: optionalString(values.addressDetail),
    Phone: optionalString(values.phone),
    PhoneDigits: optionalString(values.phoneDigits),
    Copyright: optionalString(values.copyright),
    MetaTitle: optionalString(values.metaTitle),
    MetaDescription: optionalString(values.metaDescription),
    ProposalFileName: optionalString(values.proposalFileName),
    ProposalS3Key: optionalString(values.proposalS3Key),
    LogoHeaderUrl: optionalString(values.logoHeaderUrl),
    LogoAuthUrl: optionalString(values.logoAuthUrl),
    LogoAuthInvert: values.logoAuthInvert,
    FaviconUrl: optionalString(values.faviconUrl),
    OgImageUrl: optionalString(values.ogImageUrl),
    OrderMemoTag: optionalString(values.orderMemoTag),
    IsActive: values.isActive,
    ShowQuickOrder: values.showQuickOrder,
    QuickOrderPricePolicy: values.quickOrderPricePolicy,
    ShowRegister: values.showRegister,
    DeliveryCategories: values.deliveryCategories,
  };
}

interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  required?: boolean;
  error?: string;
}

const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  ({ label, required, error, readOnly, ...props }, ref) => (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <input
        ref={ref}
        readOnly={readOnly}
        className={`h-8 w-full rounded-md border border-gray-300 px-3 text-xs outline-none focus:border-blue-500 ${
          readOnly ? "bg-gray-50 text-gray-500" : ""
        }`}
        {...props}
      />
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  ),
);

FormField.displayName = "FormField";

interface DomainListFieldProps {
  domains: string[];
  primaryHost: string;
  onChange: (next: string[]) => void;
}

// 추가 도메인(대표 host 제외) 태그 입력. Enter/추가 버튼으로 등록, 칩 X로 삭제.
function DomainListField({ domains, primaryHost, onChange }: DomainListFieldProps) {
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  const addDomain = () => {
    const normalized = normalizeHost(input);
    if (!normalized) {
      setInput("");
      return;
    }
    if (normalized === normalizeHost(primaryHost)) {
      setError("대표 도메인과 동일합니다.");
      return;
    }
    if (domains.some((d) => normalizeHost(d) === normalized)) {
      setError("이미 추가된 도메인입니다.");
      return;
    }
    onChange([...domains, normalized]);
    setInput("");
    setError(null);
  };

  const removeDomain = (target: string) => {
    onChange(domains.filter((d) => d !== target));
  };

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-700">
        추가 도메인
        <span className="ml-1 font-normal text-gray-400">
          (같은 브랜드로 접속할 다른 도메인)
        </span>
      </label>
      <div className="flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addDomain();
            }
          }}
          placeholder="예: barogologistic.com"
          className="h-8 w-full rounded-md border border-gray-300 px-3 text-xs outline-none focus:border-blue-500"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addDomain}
          className="h-8 shrink-0 px-3 text-xs font-bold"
        >
          추가
        </Button>
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      {domains.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {domains.map((domain) => (
            <span
              key={domain}
              className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] text-gray-700"
            >
              {domain}
              <button
                type="button"
                onClick={() => removeDomain(domain)}
                className="text-gray-400 hover:text-red-500"
                aria-label={`${domain} 삭제`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

interface BrandUploadFieldProps {
  fieldName: BrandUrlFieldName;
  label: string;
  register: UseFormRegister<BrandFormValues>;
  // 저장 시 일괄 업로드 전까지 폼에 보관된 asset 참조값(fileGuid 우선) 또는 저장 상태 표시값
  value: string | undefined;
  // 선택만 하고 아직 업로드 전인 파일(미리보기·파일명 표시용)
  pendingFile?: File;
  // 등록된 이미지의 presigned URL (썸네일 미리보기용). PDF는 미전달.
  registeredUrl?: string;
  isRegistered?: boolean;
  disabled: boolean;
  accept?: string;
  description: string;
  placeholder?: string;
  onSelect: (fieldName: BrandUrlFieldName, file: File) => void;
}

function BrandUploadField({
  fieldName,
  label,
  register,
  value,
  pendingFile,
  registeredUrl,
  isRegistered = false,
  disabled,
  accept = "image/*,.ico",
  description,
  placeholder = "선택된 파일 없음",
  onSelect,
}: BrandUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingFileName = pendingFile?.name;
  const hasPendingFile = Boolean(pendingFileName?.trim());
  const statusLabel = hasPendingFile
    ? "선택됨"
    : isRegistered
      ? "등록됨"
      : "미등록";
  const statusClassName = hasPendingFile
    ? "border-blue-200 bg-blue-50 text-blue-700"
    : isRegistered
      ? "border-green-200 bg-green-50 text-green-700"
      : "border-gray-200 bg-gray-50 text-gray-500";

  // 미리보기: 새로 선택한 이미지(로컬 objectURL) 우선, 없으면 등록된 presigned URL.
  // 이미지가 아닌 파일(PDF 등)은 썸네일 미대상. objectURL은 언마운트/교체 시 해제해 누수 방지.
  const pendingPreviewUrl = useMemo(
    () =>
      pendingFile && pendingFile.type.startsWith("image/")
        ? URL.createObjectURL(pendingFile)
        : null,
    [pendingFile],
  );
  useEffect(() => {
    if (!pendingPreviewUrl) return;
    return () => URL.revokeObjectURL(pendingPreviewUrl);
  }, [pendingPreviewUrl]);

  const previewUrl = pendingPreviewUrl ?? registeredUrl ?? null;

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onSelect(fieldName, file);
    }
    event.target.value = "";
  };

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-700">
        {label}
      </label>
      <input type="hidden" {...register(fieldName)} />
      <div className="flex items-center gap-2">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={`${label} 미리보기`}
              className="h-full w-full object-contain"
            />
          ) : (
            <span className="text-[10px] text-gray-400">미리보기</span>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          className="h-8 px-3 text-xs font-bold"
        >
          파일 선택
        </Button>
        <span className="min-w-0 flex-1 truncate rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
          {pendingFileName?.trim() || value?.trim() || placeholder}
        </span>
        <span
          className={`shrink-0 rounded-full border px-2 py-1 text-[11px] font-bold ${statusClassName}`}
        >
          {statusLabel}
        </span>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
      <p className="mt-1 text-xs text-gray-500">{description}</p>
    </div>
  );
}

interface BrandFormProps {
  mode: "add" | "edit";
  idx: number | null;
}

export function BrandForm({ mode, idx }: BrandFormProps) {
  const drawerOpen = useBrandStore((s) => s.drawerOpen);
  const reqAuthGuid = useAuthStore((s) => s.user?.userGuid ?? "");
  const { data: detail, isLoading: isDetailLoading } =
    useBrandDetail(idx);
  const upsertMutation = useUpsertBrand();
  const uploadMutation = useUploadBrandLogo();
  const [pendingFiles, setPendingFiles] = useState<PendingFiles>({});
  const loadedDetailIdxRef = useRef<number | null>(null);
  const lastAutoCopiedBrandNameRef = useRef<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    reset: resetForm,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<BrandFormValues>({
    resolver: zodResolver(brandSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const [activeTab, setActiveTab] = useState<BrandFormTab>("basic");

  // 검증 실패 필드가 비활성 탭에 있으면 사용자가 못 보므로, 에러가 있는 첫 탭으로 전환.
  const focusFirstErrorTab = (
    validationErrors: Partial<Record<keyof BrandFormValues, unknown>>,
  ) => {
    const erroredTab = BRAND_FORM_TAB_ORDER.find((tab) =>
      TAB_FIELDS[tab].some((field) => validationErrors[field]),
    );
    if (erroredTab) setActiveTab(erroredTab);
  };

  // pendingFiles는 컴포넌트 마운트 시 빈 {}로 시작. BrandDrawer가 key={selectedIdx}로
  // 폼을 리마운트하므로 add↔edit·다른 브랜드 전환 시 자동 초기화 → effect 리셋 불필요.
  useEffect(() => {
    if (mode === "add" && drawerOpen) {
      loadedDetailIdxRef.current = null;
      lastAutoCopiedBrandNameRef.current = null;
      resetForm(DEFAULT_FORM_VALUES);
    }
  }, [mode, drawerOpen, resetForm]);

  useEffect(() => {
    if (
      mode === "edit" &&
      detail &&
      loadedDetailIdxRef.current !== detail.idx
    ) {
      loadedDetailIdxRef.current = detail.idx;
      resetForm(toFormValues(detail));
    }
  }, [mode, detail, resetForm]);

  // 파일은 선택만 하고 보관. 실제 업로드/DB 저장은 "저장" 클릭 시 onSubmit에서 일괄 처리.
  const handleFileSelect = (fieldName: BrandUrlFieldName, file: File) => {
    const validationMessage = validateBrandAssetFile(fieldName, file);
    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }

    setPendingFiles((prev) => ({ ...prev, [fieldName]: file }));
    if (fieldName === "proposalS3Key") {
      setValue("proposalFileName", file.name, { shouldDirty: true });
    }
  };

  const handleCompanySelect = async (company: DispatchCompanyListItem) => {
    // companyGuid는 로고 저장/조회의 키라 add·edit 모두 세팅. 단 표시정보 자동복사는
    // 기존 값을 덮을 수 있어 add(신규 등록)에서만 수행.
    setValue("companyGuid", company.companyGuid, {
      shouldDirty: true,
    });

    if (mode !== "add") return;

    try {
      const companyDetail = await getDispatchCompany(company.companyGuid);
      if (getValues("companyGuid") !== company.companyGuid) return;

      const currentName = getValues("name").trim();
      const previousAutoCopiedName =
        lastAutoCopiedBrandNameRef.current?.trim() ?? "";
      const canCopyCompanyName =
        !currentName || currentName === previousAutoCopiedName;
      if (canCopyCompanyName) {
        const companyName = companyDetail.companyName ?? "";
        setValue("name", companyName, COMPANY_COPY_SET_VALUE_OPTIONS);
        lastAutoCopiedBrandNameRef.current = companyName;
      }
      setValue(
        "ceoName",
        companyDetail.representativeName ?? "",
        COMPANY_COPY_SET_VALUE_OPTIONS,
      );
      setValue(
        "bizNo",
        companyDetail.businessRegNum ?? "",
        COMPANY_COPY_SET_VALUE_OPTIONS,
      );
      setValue(
        "phone",
        companyDetail.phoneNum ?? "",
        COMPANY_COPY_SET_VALUE_OPTIONS,
      );
      setValue(
        "address",
        companyDetail.addressHq ?? "",
        COMPANY_COPY_SET_VALUE_OPTIONS,
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "알 수 없는 오류";
      toast.error(`업체 정보 조회 실패: ${message}`);
    }
  };

  const onSubmit = async (values: BrandFormValues) => {
    // URL 입력도 허용 → 순수 host(도메인)로 정규화. 예: https://b2b.nalo.kr/order → b2b.nalo.kr
    const host = normalizeHost(values.host);

    if (!reqAuthGuid) {
      toast.error("업로드 요청자 정보를 확인할 수 없습니다.");
      return;
    }

    // 1) 선택만 해둔 파일들(로고/파비콘/제안서 각 1개)을 병렬 업로드 → asset 참조값 획득.
    // 서로 독립이라 동시 업로드. 이미지만 리사이즈(대역폭 절감), 제안서(PDF)는 원본.
    const uploadedKeys: Partial<Record<BrandUrlFieldName, string>> = {};
    const companyGuid = optionalString(values.companyGuid);

    // 자산은 배차업체(companyGuid) 기준으로 저장/조회된다. 업체 없이 업로드하면 매핑이
    // 저장되지 않아 로고가 영구 미노출 → 사전 차단하고 업체 선택을 유도.
    const hasPendingFiles = Object.keys(pendingFiles).length > 0;
    if (hasPendingFiles && !companyGuid) {
      toast.error("자산을 등록하려면 기존 배차 업체를 먼저 선택해 주세요.");
      return;
    }

    try {
      const entries = Object.entries(pendingFiles) as [
        BrandUrlFieldName,
        File,
      ][];
      const results = await Promise.all(
        entries.map(async ([field, file]) => {
          const purpose = FIELD_PURPOSE[field];
          const uploadFile =
            purpose === "BRAND_PROPOSAL"
              ? file
              : await resizeBrandImage(file, purpose);
          const result = await uploadMutation.mutateAsync({
            file: uploadFile,
            BrandId: host,
            CompanyGuid: companyGuid,
            Purpose: purpose,
            ReqAuthGuid: reqAuthGuid,
          });
          return [field, result.fileGuid?.trim() || result.s3Key] as const;
        }),
      );
      for (const [field, assetReference] of results) {
        uploadedKeys[field] = assetReference;
      }
    } catch {
      // 업로드 실패 시 DB 저장 중단(useUploadBrandLogo onError가 토스트 처리)
      return;
    }

    // 2) 업로드된 asset 참조값을 폼 값에 합쳐 upsert. 수정 모드는 불변 PK idx로 기존 행 매칭.
    upsertMutation.mutate({
      ...toUpsertRequest({ ...values, ...uploadedKeys, host }),
      ...(idx != null ? { Idx: idx } : {}),
    });
  };

  const isPending = upsertMutation.isPending || uploadMutation.isPending;
  const isActive = useWatch({ control, name: "isActive" });
  const showQuickOrder = useWatch({ control, name: "showQuickOrder" });
  const showRegister = useWatch({ control, name: "showRegister" });
  const domains = useWatch({ control, name: "domains" }) ?? [];
  const hostValue = useWatch({ control, name: "host" }) ?? "";
  const logoHeaderUrl = useWatch({ control, name: "logoHeaderUrl" });
  const faviconUrl = useWatch({ control, name: "faviconUrl" });
  const ogImageUrl = useWatch({ control, name: "ogImageUrl" });
  const proposalS3Key = useWatch({ control, name: "proposalS3Key" });
  const proposalFileName = useWatch({ control, name: "proposalFileName" });
  const visibleAssetValues: Partial<
    Record<BrandUrlFieldName, string | undefined>
  > = {
    logoHeaderUrl,
    faviconUrl,
    ogImageUrl,
  };

  const existingAssetValue = (fieldName: BrandUrlFieldName) => {
    const existingByField: Partial<
      Record<BrandUrlFieldName, string | null | undefined>
    > = {
      logoHeaderUrl: detail?.logoHeaderUrl ?? detail?.logoAuthUrl,
      logoAuthUrl: detail?.logoAuthUrl,
      faviconUrl: detail?.faviconUrl,
      ogImageUrl: detail?.ogImageUrl,
      proposalS3Key: detail?.proposalUrl,
    };

    return existingByField[fieldName]?.trim() ?? "";
  };

  const isAssetRegistered = (fieldName: BrandUrlFieldName) =>
    Boolean(existingAssetValue(fieldName));

  const assetDisplayValue = (
    fieldName: BrandUrlFieldName,
    formValue: string | undefined,
  ) => {
    if (formValue?.trim()) return formValue;
    if (!existingAssetValue(fieldName)) return "";
    switch (fieldName) {
      case "logoHeaderUrl":
        return "등록된 로고";
      case "faviconUrl":
        return "등록된 Favicon";
      case "ogImageUrl":
        return "등록된 공유 미리보기 이미지";
      default:
        return "등록된 파일";
    }
  };

  if (mode === "edit" && isDetailLoading) {
    return (
      <div className="flex flex-1 items-center justify-center text-xs text-gray-400">
        로딩 중...
      </div>
    );
  }

  if (mode === "edit" && !detail) {
    return (
      <div className="flex flex-1 items-center justify-center text-xs text-gray-400">
        브랜드 정보를 찾을 수 없습니다
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit, focusFirstErrorTab)}
      className="flex flex-1 flex-col overflow-hidden"
    >
      <input type="hidden" {...register("companyGuid")} />
      <input type="hidden" {...register("brandId")} />
      <input type="hidden" {...register("metaTitle")} />
      <input type="hidden" {...register("metaDescription")} />
      <input type="hidden" {...register("copyright")} />
      <input type="hidden" {...register("orderMemoTag")} />
      <input type="checkbox" hidden {...register("logoAuthInvert")} />

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as BrandFormTab)}
        className="flex flex-1 flex-col overflow-hidden"
      >
        <TabsList className="mx-4 mt-2 w-[calc(100%-2rem)]">
          <TabsTrigger value="basic">기본 정보</TabsTrigger>
          <TabsTrigger value="display">표시 정보</TabsTrigger>
          <TabsTrigger value="asset">브랜드 자산</TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-y-auto px-4 py-2">
          {/* keepMounted: 탭 전환 시에도 모든 필드를 DOM에 유지 → react-hook-form 값/에러 보존. */}
          <TabsContent value="basic" keepMounted className="space-y-4">
        <section className="space-y-3">
          <h3 className="text-xs font-bold text-gray-900">기본 정보</h3>
          <CompanySelectField
            selectedName={mode === "edit" ? detail?.companyName ?? undefined : undefined}
            onSelect={(company) => {
              void handleCompanySelect(company);
            }}
          />
          <FormField
            label="대표 도메인(호스트)"
            required
            error={errors.host?.message}
            {...register("host", {
              // 입력 완료(포커스 아웃) 시 저장될 순수 host로 즉시 정규화해 사용자가 확인하게 함.
              onBlur: (e) => {
                const normalized = normalizeHost(e.target.value);
                if (normalized !== e.target.value) {
                  setValue("host", normalized, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }
              },
            })}
          />
          <DomainListField
            domains={domains}
            primaryHost={hostValue}
            onChange={(next) =>
              setValue("domains", next, { shouldDirty: true })
            }
          />
          <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2">
            <label className="text-xs font-medium text-gray-700">
              활성 상태
            </label>
            <Switch
              checked={isActive}
              onCheckedChange={(checked) =>
                setValue("isActive", checked, { shouldDirty: true })
              }
              size="sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2">
              <label className="text-xs font-medium text-gray-700">
                간편접수 노출
              </label>
              <Switch
                checked={showQuickOrder}
                onCheckedChange={(checked) =>
                  setValue("showQuickOrder", checked, { shouldDirty: true })
                }
                size="sm"
              />
            </div>
            <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2">
              <label className="text-xs font-medium text-gray-700">
                회원가입 노출
              </label>
              <Switch
                checked={showRegister}
                onCheckedChange={(checked) =>
                  setValue("showRegister", checked, { shouldDirty: true })
                }
                size="sm"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <span className="block text-xs font-medium text-gray-700">
              배송 카테고리 노출
            </span>
            <div
              className="grid grid-cols-2 gap-3"
              role="group"
              aria-label="배송 카테고리 노출"
            >
              {DELIVERY_CATEGORY_OPTIONS.map(({ value, label }) => (
                <label
                  key={value}
                  className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50"
                >
                  {label}
                  <input
                    type="checkbox"
                    value={value}
                    {...register("deliveryCategories")}
                    className="h-4 w-4 accent-blue-600"
                    aria-invalid={Boolean(errors.deliveryCategories)}
                  />
                </label>
              ))}
            </div>
            {errors.deliveryCategories?.message && (
              <p className="text-[11px] text-red-500">
                {errors.deliveryCategories.message}
              </p>
            )}
          </div>
          <div className="space-y-1.5 rounded-md border border-gray-200 px-3 py-2.5">
            <span className="block text-xs font-medium text-gray-700">
              간편접수 단가
            </span>
            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="간편접수 단가">
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-xs text-gray-700 has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50">
                <input
                  type="radio"
                  value="STANDARD"
                  {...register("quickOrderPricePolicy")}
                  className="h-4 w-4 accent-blue-600"
                />
                <span>
                  <strong className="font-semibold text-gray-900">ON</strong>
                  <span className="ml-1 text-gray-500">일반단가</span>
                </span>
              </label>
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-xs text-gray-700 has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50">
                <input
                  type="radio"
                  value="CONSULTATION"
                  {...register("quickOrderPricePolicy")}
                  className="h-4 w-4 accent-blue-600"
                />
                <span>
                  <strong className="font-semibold text-gray-900">OFF</strong>
                  <span className="ml-1 text-gray-500">전화 상담</span>
                </span>
              </label>
            </div>
            <p className="text-[11px] leading-4 text-gray-500">
              홈의 실시간 운임조회는 이 설정과 무관하게 API 예상 견적을 표시합니다.
            </p>
          </div>
        </section>
          </TabsContent>

          <TabsContent value="display" keepMounted className="space-y-4">
        <section className="space-y-3">
          <h3 className="text-xs font-bold text-gray-900">표시 정보</h3>
          <FormField
            label="표시명(브랜드)"
            required
            error={errors.name?.message}
            {...register("name")}
          />
          <div className="grid grid-cols-2 gap-3">
            <FormField label="대표자" {...register("ceoName")} />
            <FormField label="개인정보 책임자" {...register("privacyOfficer")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="사업자번호" {...register("bizNo")} />
            <FormField label="주선업 허가번호" {...register("licenseNo")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="노출 전화번호" {...register("phone")} />
            <FormField label="전화 연결번호(tel)" {...register("phoneDigits")} />
          </div>
          <FormField label="주소" {...register("address")} />
          <FormField label="상세 주소" {...register("addressDetail")} />
        </section>
          </TabsContent>

          <TabsContent value="asset" keepMounted className="space-y-4">
        <section className="space-y-3">
          <h3 className="text-xs font-bold text-gray-900">브랜드 자산</h3>
          {VISIBLE_ASSET_FIELDS.map((field) => (
            <BrandUploadField
              key={field.fieldName}
              fieldName={field.fieldName}
              label={field.label}
              register={register}
              value={assetDisplayValue(
                field.fieldName,
                visibleAssetValues[field.fieldName],
              )}
              pendingFile={pendingFiles[field.fieldName]}
              registeredUrl={existingAssetValue(field.fieldName) || undefined}
              isRegistered={isAssetRegistered(field.fieldName)}
              disabled={isPending}
              accept={field.accept}
              description={ASSET_LIMITS[field.fieldName].description}
              onSelect={handleFileSelect}
            />
          ))}
          <input type="hidden" {...register("proposalFileName")} />
          <BrandUploadField
            fieldName="proposalS3Key"
            label="제안서 (PDF)"
            register={register}
            value={
              proposalS3Key?.trim()
                ? proposalFileName
                : detail?.proposalUrl
                  ? proposalFileName || "등록된 제안서"
                  : ""
            }
            pendingFile={pendingFiles.proposalS3Key}
            isRegistered={isAssetRegistered("proposalS3Key")}
            accept=".pdf,application/pdf"
            description={ASSET_LIMITS.proposalS3Key.description}
            placeholder="선택된 제안서 없음"
            disabled={isPending}
            onSelect={handleFileSelect}
          />
        </section>
          </TabsContent>
        </div>
      </Tabs>

      <div className="flex gap-2 border-t border-gray-200 p-4">
        <Button
          type="submit"
          disabled={isPending}
          className="flex-1 bg-blue-600 text-sm font-bold hover:bg-blue-700"
        >
          {upsertMutation.isPending ? "저장 중..." : "저장"}
        </Button>
      </div>
    </form>
  );
}
